import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useGarage } from '../../context/GarageContext';

export const useModelSelector = (contactIdProp = null) => {
  const { contactId: contactIdFromUrl } = useParams();
  
  const contactId = contactIdProp || contactIdFromUrl;
  
  const {
    baseVehicles,
    baseMeta,
    basePartyUuid,
    vehicles,
    loading,
    error,
    fetchVehicles,
    setFilteredVehicles,
    resetVehicles,
  } = useGarage();

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const hasAttemptedLoadRef = useRef(false);

  // Validate basePartyUuid mismatch (like useAppointmentsSnapshot)
  useEffect(() => {
    if (
      contactId &&
      basePartyUuid &&
      basePartyUuid !== contactId
    ) {
      resetVehicles();
    }
    hasAttemptedLoadRef.current = false;
    setSelectedVehicle(null);
  }, [contactId, basePartyUuid, resetVehicles]);

  // Load base vehicles if needed
  const loadBase = useCallback(() => {
    if (!contactId) return;
    hasAttemptedLoadRef.current = true;
    fetchVehicles(
      {
        party_uuid: contactId,
        page: 1,
        page_size: 50,
        order_by: ["createdAt"],
        order_direction: ["DESC"],
      },
      { persistAsBase: true }
    );
  }, [contactId, fetchVehicles]);

  // Use baseVehicles when available, otherwise load them
  useEffect(() => {
    if (!contactId) return;

    if (baseVehicles.length && basePartyUuid === contactId) {
      hasAttemptedLoadRef.current = true;
      setFilteredVehicles(baseVehicles, baseMeta);
      return;
    }

    if (hasAttemptedLoadRef.current) {
      return;
    }

    loadBase();
  }, [
    contactId,
    baseVehicles.length,
    baseVehicles,
    baseMeta,
    basePartyUuid,
    loadBase,
    setFilteredVehicles,
  ]);

  // Auto-select first vehicle when vehicles are loaded
  useEffect(() => {
    if (vehicles.length > 0 && basePartyUuid === contactId && !selectedVehicle) {
      setSelectedVehicle(vehicles[0]);
    }
  }, [vehicles, selectedVehicle, contactId, basePartyUuid]);

  const selectedValue = useMemo(() => {
    if (!selectedVehicle) return null;
    const info = selectedVehicle.vehicleInformation || selectedVehicle;
    const vehicleUuid = selectedVehicle.vehicleUuid || info?.vehicleUuid;
    const label = [info?.modelYear, info?.make, info?.model]
      .filter(Boolean)
      .join(' ');
    return {
      value: vehicleUuid,
      label: label || 'Unknown Vehicle'
    };
  }, [selectedVehicle]);

  const vehicleOptions = useMemo(() => {
    return vehicles.map(vehicle => {
      const info = vehicle.vehicleInformation || vehicle;
      const vehicleUuid = vehicle.vehicleUuid || info?.vehicleUuid;
      const label = [info?.modelYear, info?.make, info?.model]
        .filter(Boolean)
        .join(' ');
      
      return {
        value: vehicleUuid,
        label: label || 'Unknown Vehicle',
        vehicle: vehicle
      };
    });
  }, [vehicles]);

  const handleVehicleChange = useCallback((vehicleId) => {
    const vehicle = vehicles.find(v => {
      const uuid = v.vehicleUuid || (v.vehicleInformation?.vehicleUuid);
      return uuid === vehicleId;
    });
    if (vehicle) {
      setSelectedVehicle(vehicle);
    }
  }, [vehicles]);

  const handleRetry = useCallback(() => {
    if (contactId) {
      hasAttemptedLoadRef.current = false;
      setSelectedVehicle(null);
      loadBase();
    }
  }, [contactId, loadBase]);

  // Use baseVehicles for snapshot-like behavior (read-only, doesn't interfere with useGaragePage)
  const snapshotLoading = loading && !baseVehicles.length;

  return {
    vehicles,
    vehicleOptions,
    selectedVehicle,
    selectedValue,
    handleVehicleChange,
    handleRetry,
    loading: snapshotLoading,
    error,
  };
};

